import { useState, useMemo } from "react";
import { fmtPct, fmtMoney, fmtMonthShort } from "../../lib/format";
import { DRIVER_RATE_PER_PERSON_MONTH } from "../../lib/constants";
import { TIERS, getTiers } from "../../lib/personnel";
import { currentMonthKey } from "../../lib/dateWindow";
import { Plus, Trash2, Plane, Lock } from "lucide-react";

// Parse "40%", "0.4" or "16h" (hours/week of a 40h FTE) into a 0..n fraction.
export const parseLoad = (raw) => {
  const s = String(raw).trim().toLowerCase();
  if (!s) return 0;
  if (s.endsWith("h")) {
    const h = Number(s.slice(0, -1)) || 0;
    return Math.round((h / 40) * 100) / 100;
  }
  let n = Number(s.replace("%", "")) || 0;
  if (s.includes("%") || n > 1.5) n = n / 100;
  return Math.round(n * 100) / 100;
};

const PILL = {
  approved:  { cls: "bg-[#EAF1EC] text-[#2C4A38] border-[#2C4A38]/30", label: "Approved" },
  proposed:  { cls: "bg-[var(--paper-soft)] text-[var(--muted-fg)] border-[var(--rule-strong)]", label: "Proposed" },
  rejected:  { cls: "bg-[#FBF3F2] text-[#6B2F2A] border-[#6B2F2A]/30", label: "Rejected" },
  removal:   { cls: "bg-[#FBF6EA] text-[#8a6d1a] border-[#8a6d1a]/30", label: "Removal requested" },
  new:       { cls: "bg-white text-[var(--soft-fg)] border-[var(--rule)]", label: "New" },
};

const TIER_ORDER = ["Managing Principal", "Principal", "Associate Principal", "Associate", "Architectural Designer"];

// Compress a person's leave into a short "on leave 15–29 Apr" summary of
// the next contiguous chunk. Empty string if nothing scheduled ahead.
function leaveSummary(byWeek) {
  if (!byWeek) return "";
  const upcoming = Object.entries(byWeek)
    .filter(([, v]) => Number(v) > 0)
    .map(([k, v]) => ({ start: new Date(k + "T00:00:00"), v: Number(v) }))
    .filter((r) => !isNaN(r.start.getTime()) && r.start.getTime() >= Date.now() - 7 * 24 * 3600 * 1000)
    .sort((a, b) => a.start - b.start);
  if (upcoming.length === 0) return "";
  const fmt = (d) => `${d.getDate()} ${d.toLocaleString("en-NZ", { month: "short" })}`;
  const first = upcoming[0];
  let last = first;
  for (let i = 1; i < upcoming.length; i += 1) {
    const gap = upcoming[i].start - last.start;
    if (gap <= 8 * 24 * 3600 * 1000) last = upcoming[i]; else break;
  }
  const endDate = new Date(last.start);
  endDate.setDate(endDate.getDate() + (last.v >= 1 ? 4 : 2));
  if (first === last && first.v < 1) return `half wk ${fmt(first.start)}`;
  return `on leave ${fmt(first.start)}–${fmt(endDate)}`;
}

// MERGED project-team grid. One grid, two row shapes:
//   · NAMED PERSON   → { personId, tier, chargeType, load, status?, ... }
//   · TBD TIER REQ   → { personId: "tbd_xxx", tbd: true, tier, chargeType, load, ... }
//
// Add flow (single "+ Add" pill):
//   pick tier → the tier's active people list + "Any [tier] — TBD" on top.
//
// Both shapes price at the tier-average rate (monthly or hourly per row's
// chargeType). TBD rows still route through the approval queue where admin
// assigns the named person on approval.
export function PersonnelAllocations({ months, people, leave = {}, rates, rows, onChange, isAdmin, canEditPast = false, rawData }) {
  const [tierPick, setTierPick] = useState("");
  const CUR = currentMonthKey();
  // Past-month editing follows the per-user canEditPastMonths flag — not
  // role. Admins without the flag (edge case) also can't edit past months.
  const isPast = (mo) => mo < CUR;
  const lockPast = (mo) => isPast(mo) && !canEditPast;
  const used = new Set(rows.map((r) => r.personId));
  const tiersPresent = TIER_ORDER.filter((t) => (people || []).some((p) => p.tier === t));
  const tierPeople = (people || []).filter((p) => p.tier === tierPick && !used.has(p.id) && p.status !== "departed");

  const nameOf = (r) => {
    if (r.tbd) return `Any ${r.tier || "tier"} — TBD`;
    return (people || []).find((p) => p.id === r.personId)?.name || r.personId;
  };
  const tierOf = (r) => r.tier || (people || []).find((p) => p.id === r.personId)?.tier || "";
  const chargeTypeOf = (r) => r.chargeType || "monthly";

  // Rate for a row — tier average at monthly OR hourly according to chargeType.
  // Falls back to the flat driver rate until the rate card is populated.
  const rateFor = (r) => {
    const tier = tierOf(r);
    const bucket = rates?.tiers?.[tier];
    const ct = chargeTypeOf(r);
    if (bucket?.count) return ct === "hourly" ? bucket.avgHourly : bucket.avgMonthly;
    if (ct === "hourly") return DRIVER_RATE_PER_PERSON_MONTH / 160;  // ~$/hour fallback
    return DRIVER_RATE_PER_PERSON_MONTH;
  };
  // Convert a row's monthly load into "billable units" for cost calc.
  // Monthly → FTE-months (load × monthly rate). Hourly → hours per month
  // (load × 160h → × hourly rate).
  const rowCostForMonth = (r, mo) => {
    const q = Number(r.load?.[mo] || 0);
    const rate = rateFor(r);
    if (chargeTypeOf(r) === "hourly") return q * 160 * rate;
    return q * rate;
  };

  const leaveHints = useMemo(() => {
    const out = {};
    for (const [pid, byWeek] of Object.entries(leave || {})) {
      const s = leaveSummary(byWeek);
      if (s) out[pid] = s;
    }
    return out;
  }, [leave]);

  const update = (idx, patch) =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const updateLoad = (idx, mo, val) =>
    onChange(rows.map((r, i) => i === idx
      ? { ...r, load: { ...(r.load || {}), [mo]: parseLoad(val) } }
      : r));
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const addPerson = (val) => {
    if (!val) return;
    const base = {
      chargeType: "monthly",
      status: undefined,
      load: {},
      tier: tierPick,
    };
    if (val === "__tbd__") {
      onChange([...rows, {
        ...base,
        personId: `tbd_${Math.random().toString(36).slice(2, 8)}`,
        tbd: true,
      }]);
    } else {
      const person = (people || []).find((p) => p.id === val);
      onChange([...rows, { ...base, personId: val, tier: person?.tier || tierPick }]);
    }
    setTierPick("");
  };

  const pillFor = (r) => {
    if (r.removalRequested) return PILL.removal;
    if (!r.status) return PILL.new;
    return PILL[r.status] || PILL.new;
  };

  const rowTotalCost = (r) => months.reduce((s, mo) => s + rowCostForMonth(r, mo), 0);
  const monthlyTotal = (mo) => rows.reduce((s, r) => s + rowCostForMonth(r, mo), 0);
  const grandCost = rows.reduce((s, r) => s + rowTotalCost(r), 0);

  // Frozen-left widths so columns align with ScopeGrid where possible.
  const W_TIER = 180, W_PERSON = 200, W_CHARGE = 96;
  const FROZEN_W = W_TIER + W_PERSON + W_CHARGE;

  return (
    <div className="border border-[var(--rule)]" data-testid="personnel-allocations">
      <div className="overflow-x-auto thin-scroll">
        <table className="text-xs border-collapse" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th className="sticky left-0 z-20 bg-[var(--paper-soft)] eyebrow text-left px-3 py-2 border-b border-r border-[var(--rule)]" style={{ width: W_TIER, minWidth: W_TIER }}>
                <div>Project team</div>
                <div className="normal-case tracking-[0.06em] text-[10px] text-[var(--soft-fg)]">tier</div>
              </th>
              <th className="sticky z-20 bg-[var(--paper-soft)] eyebrow text-left px-3 py-2 border-b border-r border-[var(--rule)]" style={{ left: W_TIER, width: W_PERSON, minWidth: W_PERSON }}>Person</th>
              <th className="sticky z-20 bg-[var(--paper-soft)] eyebrow text-left px-2 py-2 border-b border-r border-[var(--rule-strong)]" style={{ left: W_TIER + W_PERSON, width: W_CHARGE, minWidth: W_CHARGE }}>Charge</th>
              {months.map((mo) => (
                <th key={mo} className={`eyebrow text-right px-2 py-2 border-b border-[var(--rule)] ${isPast(mo) ? "bg-[var(--paper-soft)]/70 text-[var(--muted-fg)]" : "text-[var(--soft-fg)]"}`} style={{ minWidth: 90 }} title={isPast(mo) ? "Past month — locked" : undefined}>
                  {fmtMonthShort(mo)}
                  {mo === CUR && <span className="ml-1 inline-block w-1 h-1 bg-[var(--ink)] rounded-full align-middle" title="Current month (as at 1st)" />}
                </th>
              ))}
              <th className="eyebrow text-right px-3 py-2 border-b border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] sticky right-0 z-20" style={{ minWidth: 118, boxShadow: "-1px 0 0 var(--rule-strong)" }}>Row total</th>
              <th className="eyebrow text-left px-2 py-2 border-b border-[var(--rule)]" style={{ minWidth: 120 }}>Status</th>
              <th className="border-b border-[var(--rule)]" style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const pill = pillFor(r);
              const rowCost = rowTotalCost(r);
              const hint = !r.tbd && leaveHints[r.personId];
              const t = tierOf(r);
              const ct = chargeTypeOf(r);
              return (
                <tr key={r.personId} className="border-b border-[var(--rule)]" data-testid={`alloc-row-${r.personId}`}>
                  {/* Tier — dropdown for TBD, static for named */}
                  <td className="sticky left-0 z-10 bg-white px-3 py-2 border-r border-[var(--rule)]" style={{ width: W_TIER, minWidth: W_TIER }}>
                    {r.tbd ? (
                      <select
                        value={t}
                        onChange={(e) => update(idx, { tier: e.target.value })}
                        className="w-full bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white"
                        data-testid={`alloc-tier-${r.personId}`}
                      >
                        {getTiers(rawData).map((ti) => <option key={ti} value={ti}>{ti}</option>)}
                      </select>
                    ) : (
                      <div className="text-[var(--muted-fg)]" data-testid={`alloc-tier-${r.personId}`}>{t || "—"}</div>
                    )}
                  </td>
                  {/* Person */}
                  <td className="sticky z-10 bg-white px-3 py-2 border-r border-[var(--rule)]" style={{ left: W_TIER, width: W_PERSON, minWidth: W_PERSON }}>
                    <div className={r.tbd ? "text-[var(--muted-fg)] italic" : "text-[var(--ink)]"}>
                      {nameOf(r)}
                    </div>
                    {hint && (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-mono-num text-[#8a6d1a]" data-testid={`alloc-leave-hint-${r.personId}`}>
                        <Plane size={9} /> {hint}
                      </div>
                    )}
                  </td>
                  {/* Charge type */}
                  <td className="sticky z-10 bg-white p-0 border-r border-[var(--rule-strong)]" style={{ left: W_TIER + W_PERSON, width: W_CHARGE, minWidth: W_CHARGE }}>
                    <select
                      value={ct}
                      onChange={(e) => update(idx, { chargeType: e.target.value })}
                      className={`w-full px-2 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white ${ct === "hourly" ? "text-[#4a5a7a]" : ""}`}
                      data-testid={`alloc-charge-${r.personId}`}
                      title="Monthly = fraction of FTE × tier-average monthly rate · Hourly = fraction × 160h × tier-average hourly rate"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="hourly">Hourly</option>
                    </select>
                  </td>
                  {/* Month cells */}
                  {months.map((mo) => {
                    const past = isPast(mo);
                    return (
                      <td key={mo} className={`p-0 border-r border-[var(--rule)]/40 ${past ? "bg-[var(--paper-soft)]/40" : ""}`}>
                        <LoadCell
                          value={r.load?.[mo] || 0}
                          dollars={rowCostForMonth(r, mo)}
                          onCommit={(raw) => updateLoad(idx, mo, raw)}
                          testid={`alloc-${r.personId}-${mo}`}
                          readOnly={lockPast(mo)}
                        />
                      </td>
                    );
                  })}
                  {/* Row total $ */}
                  <td className="px-3 py-2 font-mono-num text-right border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] whitespace-nowrap sticky right-0 z-10" style={{ boxShadow: "-1px 0 0 var(--rule-strong)" }} data-testid={`alloc-rowtotal-${r.personId}`}>
                    {rowCost ? fmtMoney(rowCost) : "—"}
                  </td>
                  {/* Status pill */}
                  <td className="px-2 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 border text-[10px] font-display tracked-tight uppercase ${pill.cls}`}
                      title={r.status === "rejected" && r.rejectionNote ? `Management note: ${r.rejectionNote}` : undefined}
                      data-testid={`alloc-status-${r.personId}`}
                    >
                      {pill.label}
                    </span>
                    {r.status === "rejected" && r.rejectionNote && (
                      <div className="text-[10px] text-[#6B2F2A] mt-1 max-w-[140px]" data-testid={`alloc-note-${r.personId}`}>
                        “{r.rejectionNote}”
                      </div>
                    )}
                  </td>
                  <td className="p-0 text-center">
                    <button
                      onClick={() => remove(idx)}
                      className="p-2 text-[var(--soft-fg)] hover:text-[#6B2F2A]"
                      title={isAdmin ? "Remove allocation" : "Request removal (needs management approval)"}
                      data-testid={`alloc-remove-${r.personId}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={months.length + 6} className="py-8 text-center">
                  <div className="eyebrow text-[var(--soft-fg)]">No team on this project yet</div>
                  <div className="text-[11px] font-mono-num text-[var(--soft-fg)] mt-1.5 max-w-md mx-auto leading-relaxed">
                    Use <strong>+ Add</strong> below — pick a tier, then a named person or "Any [tier] — TBD".
                    {" "}
                    {isAdmin
                      ? "Your rows apply to the capacity plan immediately."
                      : "Your rows go to management for approval before they count on the capacity plan."}
                  </div>
                </td>
              </tr>
            )}
            {rows.length > 0 && (
              <tr className="bg-[var(--paper-soft)]">
                <td colSpan={3} className="sticky left-0 z-10 bg-[var(--paper-soft)] px-3 py-2 eyebrow border-t border-r border-[var(--rule-strong)]" style={{ minWidth: FROZEN_W }}>Team cost total</td>
                {months.map((mo) => {
                  const cost = monthlyTotal(mo);
                  const past = isPast(mo);
                  return (
                    <td key={mo} className={`px-2 py-2 font-mono-num text-right border-t border-[var(--rule-strong)] whitespace-nowrap ${past ? "bg-[var(--paper-soft)]/60 text-[var(--muted-fg)]" : ""}`} data-testid={`alloc-monthly-total-${mo}`}>
                      {cost ? fmtMoney(cost, { compact: true }) : "—"}
                    </td>
                  );
                })}
                <td className="px-3 py-2 font-mono-num text-right border-t border-l border-[var(--rule-strong)] bg-[var(--ink)] text-white whitespace-nowrap sticky right-0 z-10" style={{ boxShadow: "-1px 0 0 var(--rule-strong)" }} data-testid="alloc-grand-total">
                  {fmtMoney(grandCost)}
                </td>
                <td className="border-t border-[var(--rule-strong)]" colSpan={2} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="border-t border-[var(--rule)] p-2 flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 border border-[var(--rule)] px-2 py-1">
          <Plus size={11} className="text-[var(--soft-fg)]" />
          <select
            value={tierPick}
            onChange={(e) => setTierPick(e.target.value)}
            className="bg-transparent text-xs outline-none"
            data-testid="alloc-pick-tier"
          >
            <option value="">Add — pick tier…</option>
            {tiersPresent.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        {tierPick && (
          <label className="inline-flex items-center gap-2 border border-[var(--rule-strong)] px-2 py-1 bg-[var(--paper-soft)]">
            <select
              value=""
              onChange={(e) => addPerson(e.target.value)}
              className="bg-transparent text-xs outline-none"
              data-testid="alloc-pick-person"
            >
              <option value="">then person…</option>
              <option value="__tbd__">Any {tierPick} — TBD</option>
              {tierPeople.map((p) => {
                const hint = leaveHints[p.id];
                return (
                  <option key={p.id} value={p.id}>
                    {p.name}{hint ? ` — ${hint}` : ""}
                  </option>
                );
              })}
            </select>
          </label>
        )}
        <span className="text-[10px] font-mono-num text-[var(--soft-fg)]">
          Cells take <span className="text-[var(--ink)]">40%</span>, <span className="text-[var(--ink)]">0.4</span> or <span className="text-[var(--ink)]">16h</span> (hours/week). Cost = fraction × tier-average rate (monthly or hourly per row).{" "}
          {isAdmin
            ? "Rows apply to the capacity plan immediately."
            : "Rows — including TBD-by-tier — go to management for approval before they count on the capacity plan."}
        </span>
      </div>
    </div>
  );
}

function LoadCell({ value, dollars, onCommit, testid, readOnly = false }) {
  const [draft, setDraft] = useState(null);
  if (readOnly) {
    return (
      <div
        className="relative w-full px-1.5 py-2 font-mono-num text-xs text-right text-[var(--muted-fg)] min-h-[34px]"
        data-testid={testid}
        title="Past month — locked"
      >
        {value > 0 ? (
          <>
            <div className="inline-flex items-center gap-1 justify-end">
              <Lock size={8} className="opacity-50" />
              {fmtPct(value, 0)}
            </div>
            {dollars > 0 && (
              <div className="px-0 pb-0 text-[9px] font-mono-num text-[var(--soft-fg)] text-right leading-none">
                {fmtMoney(dollars, { compact: true })}
              </div>
            )}
          </>
        ) : (
          <span className="text-[var(--rule-strong)]">—</span>
        )}
      </div>
    );
  }
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={draft ?? (value ? fmtPct(value, 0) : "")}
        onFocus={() => setDraft(value ? String(Math.round(value * 100)) : "")}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (draft !== null) onCommit(draft); setDraft(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        placeholder="—"
        className="w-full px-1.5 py-2 bg-transparent font-mono-num text-xs text-right outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white placeholder:text-[var(--rule-strong)]"
        data-testid={testid}
      />
      {value > 0 && dollars > 0 && draft === null && (
        <div className="px-1.5 pb-1 text-[9px] font-mono-num text-[var(--soft-fg)] text-right leading-none">
          {fmtMoney(dollars, { compact: true })}
        </div>
      )}
    </div>
  );
}
